/**
 * AssignPanel — 시험지 학생 배포 + 시험 정보(계약 authoring §3·§4 · exam-papers §5-2).
 *
 * 배포는 사실상 되돌릴 수 없다. 그 시점의 문항·본문이 스냅샷으로 고정되고 학생마다 배포본이
 * 따로 생긴다. 그래서 [배포]는 인라인 확인 바를 한 번 거친다 — window.confirm 은 닫히는 순간
 * "무엇을 누구에게" 라는 근거가 사라져, 되돌릴 수 없는 조작의 확인창으로 부적절하다.
 *
 * 배포 목록은 GET /papers/{id}/assignments 하나로 그린다(contract-answers §1-1).
 *
 * ⛔ 기능 보류 — 시험 정보 입력(2026-09-05, 오너 지시. 추후 기능 오픈 예정)
 *   시험구분·차수·시험일자·예약 오픈·자동 종료를 **받지도 보내지도 않는다**. 입력 칸·상태·요약·
 *   시각 역전 경고·배포 요청 적재를 전부 주석으로 재웠고, 자리마다 「⛔ 기능 보류」로 표시했다.
 *   되살릴 때는 이 파일에서 그 표시를 훑어 되돌리면 된다 — 계약(authoring §4)과 직렬화
 *   (examPaperApi.examInfoBody·toOffsetIso)는 손대지 않았으므로 그대로 다시 선다.
 *   **표시는 닫지 않았다**(오너 결정 2026-09-05): 출제 이력 줄의 시험일·오픈 예정·자동 종료와
 *   채점 화면(AssignmentPanel)의 예약 잠금은 서버가 내려준 사실을 읽는 자리라, 값이 붙은
 *   배포본이 남아 있으면 화면이 그 사실을 숨겨선 안 된다.
 *
 * ⛔ 기능 보류 — 「예약생」배지(2026-09-05, 오너 지시. Q19 회신 뒤 다시 세운다)
 *   student_status 200 을 학생 행·칩에 배지로 붙이던 것을 재웠다. 이 배지를 요청한 원래 이유는
 *   「퇴원생이 재원생과 똑같이 보인다」였는데(FE-20260828 2-1), 회신이 그 전제를 깼다 —
 *   퇴원은 is_deleted 이고 목록이 삭제 학생을 애초에 빼고 준다(BE-20260829 §1 2-1). 원래 물음은
 *   배지 없이 해소됐고, 남은 200(예약 중, dev 19/631)은 곁다리로 딸려온 값이다.
 *   **예약 중 학생에게 배포해도 되는지는 아직 답이 없다**(FE-20260829-reply-followup Q19 회신 대기).
 *   그래서 지금 모양은 어느 답이 와도 틀리다 — 서버가 막는다면 배지가 아니라 고르는 자리의
 *   **잠금과 이유**여야 하고(「눌러서 알게 되는 금지를 만들지 않겠다」는 Q19 에 우리가 쓴 말이다),
 *   받는다면 사람이 할 행동이 안 바뀌어 줄만 시끄러워진다.
 *   examPaperApi 의 STUDENT_STATUS·studentStatusDisplay 정의는 **그대로 둔다** — 회신이 오면
 *   그 답에 맞는 모양으로 다시 세울 때 새로 쓸 이유가 없다. 여기 호출부 두 곳만 잠갔다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// ⛔ 기능 보류(시험 정보) — CalendarClock 은 잠든 시험 정보 칸에서만 쓰였다. 되살릴 때 함께 되돌린다
import { Search, Send, X, Users, AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../common';
import SortableTh from '../common/SortableTh';
import { nextSort, sortRows, numSortValue, textSortValue, timeSortValue } from '../common/tableSort';
import { useAuth } from '../../context/AuthContext';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(문제 v2 목록 표가 기준 얼굴이다).
// 가드와 꾸밈 클릭 판정은 학생명 링크도 같이 쓴다 — 같은 화면을 떠나는 길은 같은 규칙을 지나야 한다.
import ProblemIdLink, { useNavGuard, isModifiedClick } from '../ProblemHubV2/ProblemIdLink';
import {
  assignPaper, fetchPaperAssignments, searchStudents, studentId, studentName,
  // ⛔ 기능 보류(예약생 배지) — studentStatusDisplay 는 잠든 배지에서만 쓰였다. 되살릴 때 함께 되돌린다
  assignStatusDisplay, fmtServerDateTime, fmtServerDateShort, describeApiError, errorReason,
  // 결과표가 적는 숫자의 근거 — 검산은 assignmentStats.test.js 에 있다
  assignmentCounts, accuracyOf, accuracyTone, assignmentsSummary,
  accuracyBasisText, accuracyEmptyText,
  studentSchoolText, studentClassText,
  STUDENT_NO_TEACHER, AUTH_REDIRECT } from './examPaperApi';

const PER_PAGE = 20;

/**
 * 「검색 결과 전체 선택」이 한 번에 받아 오는 쪽 크기 — **계약값이지 취향이 아니다.**
 * 학생 검색은 per_page 기본 20 · **최대 200**(BE-20260826-audit-fixes §7 113행).
 * 상한을 넘겨 부르면 서버가 조용히 잘라 주므로, 「다 받았다」고 믿은 채 일부만 담게 된다.
 */
const BULK_PER_PAGE = 200;
/**
 * 통째로 담을 수 있는 쪽 수의 한도(200×20 = 4,000명). 배포는 되돌릴 수 없는 조작이라,
 * 조건을 하나도 안 걸고 누른 실수가 학원 전체로 번지지 않게 상한을 둔다.
 * 넘치면 **담은 수를 화면에 적는다** — 다 담은 척하지 않는다.
 */
const BULK_MAX_PAGES = 20;
/**
 * 「배포할 학생」 칩을 한 번에 몇 개까지 펼쳐 둘지. 전체 선택이 생기면서 명단이 수백 명이 될 수
 * 있는데, 칩을 전부 그리면 [출제] 버튼이 화면 밖으로 밀려난다 — 누르기 직전에 명단이 눈에
 * 있어야 한다는 원래 배치(2026-08-28 오너 지시)가 거꾸로 깨진다.
 */
const CHIP_LIMIT = 20;

// ⛔ 기능 보류(시험 정보) — 추후 기능 오픈 예정
// const EMPTY_EXAM_INFO = { examType: '', examRound: '', examDate: '', openAt: '', closeAt: '' };

// 빈 검색 조건 — 네 칸이 되면서 자리마다 손으로 적으면 한 곳을 빠뜨린다(초기화가 반만 비운다)
const EMPTY_COND = { q: '', school: '', lecture: '', teacher: '' };

/**
 * 「지금 화면의 결과가 어느 조건으로 찾은 것인가」를 한 줄로 만든다.
 * 전체 선택은 **그 조건의 결과 전체**를 담은 것이라, 조건이 바뀌면 그 사실은 더 이상 지금 화면에
 * 대한 말이 아니다. 담긴 학생은 그대로 두고 머리줄의 체크만 꺼야 하므로 비교할 자가 필요하다.
 */
const condKeyOf = (c) => JSON.stringify([c?.q ?? '', c?.school ?? '', c?.lecture ?? '', c?.teacher ?? '']);

/**
 * ⛔ 화면에서 내림 — 시험 일자(2026-09-06, 위 examLabel 과 같은 묶음)
 *
 * 되살릴 때 규칙까지 함께 되돌리라고 남긴다. **시험 일자는 날짜만이라 그대로 잘랐다** — 시각이
 * 없으니 지역시간으로 옮길 것도 없다. 계약 §1-1 이 배정의 exam_date 에도 'Z' 를 붙인다고 했지만
 * 그대로 뒀는데, 자정 UTC 를 지역시간으로 되찍으면 음수 오프셋 지역에서 날짜가 하루 밀리기
 * 때문이다. 앞 10자는 서버가 뜻한 달력 날짜 그 자체라 존이 붙든 안 붙든 같은 답을 낸다.
 * 같은 이유로 **마감일에는 이 자를 쓰면 안 된다** — closed_at 은 시각이 붙은 UTC 라 문자열을
 * 자르면 날짜가 어긋난다. 그쪽은 fmtServerDateShort 가 파싱해서 다시 찍는다.
 *   const fmtDate = (s) => (s ? String(s).slice(0, 10) : '');
 */

/**
 * 상세의 문항 배열 — 응답이 키를 못박지 않아 형제(PaperItemsEditor:36 pickItems)가 세 이름을 본다.
 * 여기서는 **409 가 돌려준 problem_id 에 문항 번호를 붙일 때만** 쓰므로, 못 알아보면 빈 배열이면 된다
 * (번호 없이 문제 번호만 보이고 끝난다 — 편집기와 달리 여기서 잘못 읽어도 지워지는 것이 없다).
 */
function paperItems(p) {
  if (Array.isArray(p?.items)) return p.items;
  if (Array.isArray(p?.problems)) return p.problems;
  if (Array.isArray(p?.snapshot?.items)) return p.snapshot.items;
  return [];
}

/** 검색 결과 행의 오른쪽 꼬리 — 학교·학년에 담당·강의를 잇는다(계약 §7 응답 필드) */
function studentMeta(s) {
  const parts = [];
  const school = [s?.school_name, s?.grade]
    .filter((v) => v != null && String(v).trim() !== '')
    .join(' ');
  if (school) parts.push(school);
  if (s?.in_charge_teacher) parts.push(`담당 ${s.in_charge_teacher}`);
  if (s?.lecture) parts.push(String(s.lecture));
  return parts.join(' · ');
}

/**
 * ⛔ 화면에서 내림 — 시험 구분·차수(2026-09-06 오너 결정)
 *
 * `exam_type` 은 시험지의 속성이 아니라 배정의 속성이고, 백엔드가 리포트에서 「이 원소는 v2 시험지
 * 배정이다」를 가리키는 판별자로도 쓴다(`exam_type:'paper_v2'`). 사람이 읽을 값이 아니라서 그 자리에
 * **출제된 시험지의 버전**(`paper_revision`)을 세웠다. 시험일(`exam_date`)도 같은 묶음이라 함께 내렸다 —
 * 시험 정보 입력 자체가 2026-09-05 부터 잠겨 있어 새 배포에는 영영 값이 없다.
 * 되살릴 때는 이 함수와 표의 열을 함께 되돌린다:
 *   function examLabel(row) { exam_type + exam_round + '차' }
 */

/**
 * 출제 이력 표의 머리. 「결과」 칸만 빼고 누르면 줄이 선다(내림차순 → 오름차순 → 기본, common/tableSort) —
 * 결과 칸은 채점 창을 여는 버튼뿐이라 세울 값이 없다. 목록은 페이지 없이 통째로 오므로(loadAssignments)
 * 브라우저 안의 정렬이 곧 전체 차례다. 기본은 서버가 준 차례다.
 * 머리 줄 글자 맞춤(left·center)은 종전 그대로 옮겼다.
 */
const HISTORY_COLUMNS = [
  { key: 'aid', label: '#', align: 'text-center' },
  { key: 'sid', label: '학생 ID', align: 'text-center' },
  { key: 'name', label: '학생명', align: 'text-left' },
  { key: 'school', label: '학교·학년', align: 'text-left' },
  { key: 'klass', label: '담당·강의', align: 'text-left' },
  { key: 'status', label: '상태', align: 'text-center' },
  { key: 'rev', label: '버전', align: 'text-center' },
  { key: 'counts', label: '정답·오답·총', align: 'text-center' },
  { key: 'rate', label: '정답률', align: 'text-center' },
  { key: 'close', label: '마감일', align: 'text-center' },
  { key: 'result', label: '결과', align: 'text-center', sortable: false },
];

/**
 * 출제 이력 한 줄의 정렬 값 — 칸이 **그린 값**으로 세운다.
 *   상태: 예약(50) → 진행 중(100) → 채점 완료(200) 의 흐름 차례. status 가 비면 칸과 같이 is_closed 로 받친다.
 *   정답·오답·총: 앞의 **정답 수**로 세운다. 정답·오답을 못 받은 줄(칸에 「채점 n/m」)은 값 없음 — 맨 아래다.
 *   정답률: 마감분에만 값이 있다(accuracyOf) — 진행 중인 줄은 맨 아래다.
 *   마감일: 칸이 보이는 날짜 — 마감됐으면 마감 시각, 아니면 자동 종료 예정.
 */
function assignmentSortValue(r, key) {
  switch (key) {
    case 'aid': return numSortValue(r?.assignment_id);
    case 'sid': return numSortValue(r?.student_id);
    case 'name': return textSortValue(r?.student_name);
    case 'school': return textSortValue(studentSchoolText(r));
    case 'klass': return textSortValue(studentClassText(r));
    case 'status': {
      const s = numSortValue(r?.status);
      if (s === 50 || s === 100 || s === 200) return s;
      return r?.is_closed ? 200 : null;
    }
    case 'rev': return numSortValue(r?.paper_revision);
    case 'counts': {
      const { correct, wrong } = assignmentCounts(r);
      return correct != null && wrong != null ? correct : null;
    }
    case 'rate': return accuracyOf(r);
    case 'close': return timeSortValue(r?.closed_at || r?.close_at);
    default: return null;
  }
}

/** 학생 ID 는 정수 계약(student_ids:[int]) — 문자열로 들고 있어도 보낼 때 되돌린다 */
function toIntId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

/**
 * 학생 번호 — 학생관리 표와 같은 동그란 초록 번호다(오너 지시 2026-09-20).
 * 클래스·title·값 없을 때의 '-' 까지 「전체 테스트」 표(StudentsHub/AssignmentFeedTable)·문제 상세
 * [학생] 탭(ProblemHubV2/SolverTable)과 같은 값이다 — 같은 학생이 화면마다 다른 얼굴로 서면
 * 같은 사람인 줄 모른다.
 *
 * 이 표에서 특히 그랬다: 왼쪽 첫 칸의 **배정 번호**(#34)와 이 칸이 둘 다 회색 고정폭 숫자라
 * 서로 같은 종류의 번호로 읽혔다. 번호가 둘이면 얼굴로 갈라야 한다.
 * 칸이 가운데 정렬이라 원도 가운데로 세운다(mx-auto) — 형제 표는 첫 칸 왼쪽에 서므로 거기엔 없는 값이다.
 */
function StudentIdBadge({ id }) {
  return (
    <div
      className="mx-auto flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white"
      title={id != null ? `학생 ID: ${id}` : '학생 정보 없음'}
    >
      {id ?? '-'}
    </div>
  );
}

/**
 * 학생명 — 그 학생의 학습 화면으로 가는 길(오너 지시 2026-09-20).
 * 얼굴은 종전 그대로 두고 길만 얹는다. 이 표에서 학생을 찾은 다음 할 일이 「이 학생은 요즘 어떤가」인데,
 * 지금까지는 메뉴로 나가 학생을 다시 검색해야 했다.
 *
 * button 이 아니라 진짜 <a> 다 — 문제 번호 링크(ProblemIdLink)와 같은 이유로, ⌘·Ctrl 클릭으로
 * 새 탭에 여는 것은 브라우저가 하는 일이고 button 으로는 그 길이 아예 없다. 채점하며 학생 화면을
 * 옆 탭에 띄워 두는 것이 이 표에서 가장 그럴듯한 쓰임이다.
 *
 * 세 가지를 가로챈다:
 *   ① 행 전체가 **다른 곳**(채점 창)으로 가는 길이라, 이름을 누른 것이 두 가지를 동시에 시키면 안 된다
 *   ② 꾸밈 클릭(새 탭·새 창)은 그냥 보낸다 — 가로채면 위에 적은 그 길이 막힌다
 *   ③ 담아 둔 학생 명단은 이 패널 안에서만 산다. 화면이 깔아 둔 가드(ProblemNavGuard)에 먼저 묻는다
 *
 * 학생 번호가 없으면(삭제된 학생 — 신상 6필드가 통째로 null 로 온다, 회신 §1) 링크로 만들지 않는다.
 * 갈 곳이 없는 링크는 누른 사람에게 빈 화면을 준다.
 */
function StudentNameLink({ id, name }) {
  const guard = useNavGuard();
  const label = name || '(이름 없음)';
  if (id == null) return <span className="font-medium text-gray-900">{label}</span>;
  return (
    <Link
      to={`/students-hub/student/${id}`}
      title={`${label} 학생의 학습 화면 열기`}
      onClick={(e) => {
        e.stopPropagation();
        if (isModifiedClick(e)) return;
        if (guard && !guard()) e.preventDefault();
      }}
      className="font-medium text-gray-900 underline-offset-2 hover:text-primary hover:underline"
    >
      {label}
    </Link>
  );
}

/**
 * @param {number} refreshToken 값이 바뀌면 배포 목록만 다시 읽는다. 채점 창에서 저장·마감하면
 *   이 표의 숫자(정답·오답·상태·마감일)가 낡는데, 창은 이 패널의 자식이 아니라 페이지가 띄우므로
 *   여기서 그 사실을 알 길이 없다. 페이지가 토큰을 올려 알린다.
 */
export default function AssignPanel({ paperId, paper, onOpenAssignment, paperDirty = false, onDirtyChange = null, refreshToken = 0 }) {
  /**
   * 학생 피커는 GET /api/v2/students 하나로 간다(계약 §7). **범위를 토큰이 정한다** —
   * 매니저·관리자는 전체, 교사는 자기 담당. 그래서 계정 종류(userType·userId)를 넘기던 인자가
   * 사라졌고, 화면도 계정 종류를 **추측하지 않는다** — 응답의 scope('all'|'mine')를 그대로 읽는다.
   */
  const { getAccessToken } = useAuth();
  const [q, setQ] = useState('');
  const [school, setSchool] = useState('');
  const [lecture, setLecture] = useState('');
  const [teacher, setTeacher] = useState('');
  // 담당 없는 학생만 — 계약이 in_charge_teacher='none' 이라는 예약어를 쓴다(§7). 켜면 입력칸은 잠근다
  const [noTeacher, setNoTeacher] = useState(false);
  const [rows, setRows] = useState({ items: [], page: 1, totalPages: 1, totalItems: 0 });
  // 서버가 말한 이 토큰의 범위(§7). 첫 검색 전에는 null — 그때는 "전체/담당" 중 어느 쪽인지 단정하지 않는다
  const [scope, setScope] = useState(null);
  const [searched, setSearched] = useState(false); // 첫 검색 전과 "결과 없음"을 구분한다
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState([]); // 학생 객체를 통째로 — 페이지를 넘겨도 이름을 잃지 않는다
  /**
   * 「검색 결과 전체」로 담은 명단 — `{ condKey, keys:[학생 키] }`.
   * 이 기억이 있어야 머리줄 체크가 **사실**을 말한다. 담은 뒤 칩에서 한 명을 빼면 「결과 전체가
   * 담겼다」는 더 이상 참이 아니므로 체크가 풀린다. 체크를 끌 때 빼는 것은 **그 검색 결과**이지
   * 명단 전체가 아니다(레거시 형제 StudentAssignModal:169 의 「필터된 학생만 해제」와 같은 규칙) —
   * 다른 조건으로 검색해 고른 학생은 그대로 남는다. 명단을 통째로 비우는 것은 [모두 비우기]다.
   */
  const [bulk, setBulk] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const bulkBusyRef = useRef(false); // 이중 클릭 차단 — 여러 쪽을 받는 동안 또 누르면 왕복이 겹친다
  const [chipsExpanded, setChipsExpanded] = useState(false); // 명단 칩을 다 펼쳤는가
  // ⛔ 기능 보류(시험 정보) — 추후 기능 오픈 예정
  // const [examInfo, setExamInfo] = useState(EMPTY_EXAM_INFO);
  const [confirming, setConfirming] = useState(false);
  const [deploying, setDeploying] = useState(false);

  // 고른 학생 명단과 시험 정보는 여기서만 산다 — 시험지를 옮기거나 닫으면 통째로 사라진다.
  // 부모가 이동 전에 물을 수 있도록 알린다(해제는 언마운트 정리가 한다).
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;
  // ⛔ 기능 보류(시험 정보) — 지금 「고친 것」은 고른 학생뿐이다. 되살릴 때 examInfo 항을 되돌린다
  const formDirty = picked.length > 0;
  //  || Object.values(examInfo).some((v) => String(v ?? '').trim() !== '');
  useEffect(() => { onDirtyRef.current?.(formDirty); }, [formDirty]);
  useEffect(() => () => onDirtyRef.current?.(false), []);
  const [toast, setToast] = useState(null);
  /**
   * 배포 실패 — 문장 하나로 흘리지 않고 통째로 붙든다(계약 §1-3).
   * 409 는 **어느 문항이 걸렸는지**를 problem_ids 로 알려 주는데, 그걸 안 보이면 사용자는
   * "삭제된 문항이 있습니다"만 읽고 무엇을 뺄지 모른 채 같은 버튼을 다시 누른다.
   * { text, hint, items:[{pid, no}] } — no 는 이 시험지에서의 문항 번호(order_no), 못 찾으면 null.
   */
  const [deployError, setDeployError] = useState(null);

  // 이 시험지의 배포 목록
  const [assignments, setAssignments] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  // 방금 배포한 것 — 응답의 assignment_id 로 목록에서 바로 짚을 수 있다(§2-9)
  const [justCreated, setJustCreated] = useState([]);
  // 출제 이력 머리 정렬 — 새로고침·배포로 목록이 다시 와도 고른 칸은 남는다
  const [historySort, setHistorySort] = useState(null);
  const sortedAssignments = useMemo(
    () => sortRows(assignments, historySort, assignmentSortValue),
    [assignments, historySort],
  );
  const onHistorySort = useCallback((key) => setHistorySort((prev) => nextSort(prev, key)), []);

  const seqRef = useRef(0);
  const listSeqRef = useRef(0);
  const aliveRef = useRef(true);
  const deployingRef = useRef(false); // 이중 제출 차단 — setState 는 비동기라 disabled 만으론 늦다
  // StrictMode 는 마운트 → cleanup → 마운트를 돌린다 — 마운트마다 세우지 않으면 dev 에서 false 로 굳는다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const paperTitle = paper?.title ?? paper?.paper_title ?? '';
  /**
   * 지금 시험지의 판. 배정본이 굳힌 판(items[].paper_revision — 2026-08-29 회신 §1-1 로 목록에 실렸다)과
   * 견주는 자를 여기 한 벌만 둔다. 부모가 시험지 상세를 통째로 내려주므로 값은 이미 손에 있다.
   * 둘 중 하나라도 없으면 **견주지 않는다** — 모르는 것을 「같다/다르다」로 말하지 않는다.
   */
  const currentRevision = paper?.revision ?? null;
  const revDiffers = (r) => currentRevision != null && r?.paper_revision != null
    && String(r.paper_revision) !== String(currentRevision);

  /**
   * override 를 받는 이유 — setState 직후에 부르면 이 콜백은 **옛 값을 붙든 클로저**다.
   * [조건 비우기]처럼 「비우고 곧바로 다시 찾기」를 하려면 새 조건을 인자로 건네야 한다.
   * 안 그러면 입력칸은 비었는데 결과는 옛 조건 그대로 남아, 화면이 사실과 다른 말을 한다.
   */
  const search = useCallback(async (page, override = null) => {
    const cond = override || { q, school, lecture, teacher: noTeacher ? STUDENT_NO_TEACHER : teacher };
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const d = await searchStudents({ ...cond, page, perPage: PER_PAGE }, getAccessToken);
      if (!aliveRef.current || seq !== seqRef.current) return; // 늦게 온 응답이 최신 결과를 덮지 않게
      setRows({
        items: d.items,
        page: d.page ?? page,
        totalPages: d.totalPages ?? 1,
        totalItems: d.totalItems ?? d.items.length,
      });
      // scope 는 토큰이 정하는 값이라 매번 같지만, 받은 것만 믿는다 — 안 오면 이전 값을 지운다
      setScope(d.scope ?? null);
      /**
       * 조건이 달라졌으면 직전 전체 선택은 지금 결과에 대한 말이 아니다 — 머리줄 체크만 끄고
       * **담긴 학생은 건드리지 않는다**(고른 명단을 검색이 지우면, 조건을 바꿔 두 반을 이어
       * 고를 수가 없다). 쪽만 넘긴 경우는 조건이 같으므로 체크가 그대로 살아 있다.
       */
      const ck = condKeyOf(cond);
      setBulk((prev) => (prev && prev.condKey === ck ? prev : null));
    } catch (e) {
      if (!aliveRef.current || seq !== seqRef.current) return;
      setError(e.message || '학생을 검색하지 못했습니다.');
      setRows({ items: [], page: 1, totalPages: 1, totalItems: 0 });
    } finally {
      if (aliveRef.current && seq === seqRef.current) setLoading(false);
    }
  }, [q, school, lecture, teacher, noTeacher, getAccessToken]);

  // 검색 조건이 하나라도 들어 있는가 — [초기화]를 켜고 끄는 판정(칸이 넷이라 손으로 세지 않는다)
  const hasCond = q !== '' || school !== '' || lecture !== '' || teacher !== '' || noTeacher;

  const resetCond = () => {
    setQ(''); setSchool(''); setLecture(''); setTeacher(''); setNoTeacher(false);
    search(1, EMPTY_COND); // 비운 조건으로 즉시 다시 찾는다 — 칸과 결과가 어긋나지 않게
  };

  /**
   * 화면이 지금 보고 있는 범위(§7). **계정 종류를 프론트가 추측하지 않는다** —
   * 첫 검색 전에는 서버가 말해 준 적이 없으므로 "볼 수 있는 학생 전체"라고만 쓴다.
   */
  const scopeText = scope === 'all' ? '전체 학생' : (scope === 'mine' ? '담당 학생' : '볼 수 있는 학생 전체');

  const loadAssignments = useCallback(async () => {
    if (paperId == null) return;
    const seq = (listSeqRef.current += 1);
    setListLoading(true);
    setListError(null);
    try {
      // 페이지네이션이 계약에 없어 전량 전제 — 응답의 페이지 값은 아직 화면이 쓰지 않는다
      const d = await fetchPaperAssignments(paperId, getAccessToken);
      if (!aliveRef.current || seq !== listSeqRef.current) return;
      setAssignments(d.items);
    } catch (e) {
      if (!aliveRef.current || seq !== listSeqRef.current) return;
      setListError(e.message || '배포 목록을 불러오지 못했습니다.');
      setAssignments([]);
    } finally {
      if (aliveRef.current && seq === listSeqRef.current) setListLoading(false);
    }
  }, [paperId, getAccessToken]);

  useEffect(() => { loadAssignments(); }, [loadAssignments, refreshToken]);

  const toggle = (s) => {
    const key = String(studentId(s));
    setPicked((prev) => (prev.some((p) => String(studentId(p)) === key)
      ? prev.filter((p) => String(studentId(p)) !== key)
      : [...prev, s]));
    setConfirming(false); // 확인 바가 떠 있는 동안 명단이 바뀌면 확인한 내용과 달라진다
    setDeployError(null); // 명단이 달라지면 직전 실패는 더 이상 지금 보낼 것에 대한 말이 아니다
  };

  // 고른 학생 키 — 전체 선택이 생기면서 명단이 수백 명이 될 수 있어, 행마다 훑지 않고 한 벌로 만든다
  const pickedKeys = useMemo(() => new Set(picked.map((p) => String(studentId(p)))), [picked]);
  const isPicked = (s) => pickedKeys.has(String(studentId(s)));

  /**
   * 머리줄 체크의 세 상태 — **화면이 담기지 않은 것을 담겼다고 말하지 않게** 한다.
   *   켜짐    : 전체 선택으로 담은 명단이 통째로 살아 있다
   *   반쯤    : 지금 쪽에 고른 학생이 섞여 있다(손으로 골랐든, 명단에서 한 명을 뺐든)
   *   꺼짐    : 지금 쪽에 고른 학생이 하나도 없다
   * 「결과 전체가 담겼는가」는 지금 쪽만 봐서는 알 수 없다 — 그래서 bulk 의 기억으로 판정한다.
   */
  const bulkOn = bulk != null && bulk.keys.length > 0 && bulk.keys.every((k) => pickedKeys.has(k));
  const pagePickedCount = rows.items.reduce((n, s) => n + (isPicked(s) ? 1 : 0), 0);
  const bulkPartial = !bulkOn && pagePickedCount > 0;
  const bulkCheckRef = useCallback((el) => { if (el) el.indeterminate = bulkPartial; }, [bulkPartial]);

  /** 담은 결과를 명단에 더한다 — 이미 고른 학생은 두 번 들어가지 않는다 */
  const addAllToPicked = (list, condKey) => {
    setPicked((prev) => {
      const has = new Set(prev.map((p) => String(studentId(p))));
      return [...prev, ...list.filter((s) => !has.has(String(studentId(s))))];
    });
    setBulk({ condKey, keys: list.map((s) => String(studentId(s))) });
    setConfirming(false); // 확인 바가 가리키던 명단과 달라진다
    setDeployError(null);
  };

  /**
   * 검색 결과 **전체** 담기·빼기(오너 결정 2026-09-16 — 「지금 쪽 20명」이 아니라 결과 전체다).
   * 화면은 한 쪽에 20명만 받지만 선택은 결과 전체를 뜻하므로, 여러 쪽이면 나머지를 서버에서
   * 더 받아 온다(한 번에 200명 — 계약 상한). 받는 도중 검색을 다시 하면 seq 가 어긋나 버린다.
   */
  const toggleAllResults = async () => {
    if (bulkBusyRef.current || loading) return;
    // 이미 통째로 담겨 있다 → **그 검색 결과만** 뺀다(다른 조건으로 고른 학생은 그대로 둔다)
    if (bulkOn) {
      const keys = new Set(bulk.keys);
      setPicked((prev) => prev.filter((p) => !keys.has(String(studentId(p)))));
      setBulk(null);
      setBulkError(null);
      setConfirming(false);
      setDeployError(null);
      return;
    }
    const cond = { q, school, lecture, teacher: noTeacher ? STUDENT_NO_TEACHER : teacher };
    const condKey = condKeyOf(cond);
    setBulkError(null);
    // 한 쪽뿐이면 결과가 이미 손에 있다 — 서버를 다시 부르지 않는다
    if (rows.totalPages <= 1) { addAllToPicked(rows.items, condKey); return; }

    const seq = seqRef.current; // 검색이 새로 돌면(seq 증가) 이 수집은 버린다
    bulkBusyRef.current = true;
    setBulkLoading(true);
    try {
      const all = [];
      let page = 1;
      let pages = 1;
      do {
        // 쪽을 **이어** 받는다 — 한꺼번에 던지면 같은 조건의 왕복 수십 개가 동시에 서버를 친다
        const d = await searchStudents({ ...cond, page, perPage: BULK_PER_PAGE }, getAccessToken);
        if (!aliveRef.current || seq !== seqRef.current) return;
        all.push(...d.items);
        pages = Math.min(d.totalPages ?? 1, BULK_MAX_PAGES);
        page += 1;
      } while (page <= pages);
      addAllToPicked(all, condKey);
      // 한도에 걸려 덜 담았으면 **담은 수를 말한다** — 다 담은 척하면 빠진 학생이 조용히 사라진다
      if ((rows.totalItems ?? 0) > all.length) {
        setBulkError(`결과가 많아 앞 ${all.length}명만 담았습니다 — 조건을 좁혀 다시 담으세요.`);
      }
    } catch (e) {
      if (!aliveRef.current || seq !== seqRef.current) return;
      setBulkError(e.message || '검색 결과를 모두 담지 못했습니다 — 다시 시도해 주세요.');
    } finally {
      bulkBusyRef.current = false;
      if (aliveRef.current) setBulkLoading(false);
    }
  };

  // ⛔ 기능 보류(시험 정보) — 추후 기능 오픈 예정
  // const setField = (key, value) => {
  //   setExamInfo((prev) => ({ ...prev, [key]: value }));
  //   setConfirming(false); // 확인 바가 가리키던 시험 정보와 달라진다
  // };

  /**
   * problem_id → 이 시험지에서의 문항 번호. **items 의 배열 순서가 곧 출제 순서**다
   * (PaperItemsEditor:4 의 전제와 같다). 409 가 돌려준 problem_id 를 그대로 띄우면
   * "#1204 를 빼라"가 되는데, 화면에 보이는 것은 「3번 문항」이라 짚을 수가 없다.
   * 상세는 부모가 이미 읽어 prop 으로 들고 있으므로 왕복은 필요 없다.
   */
  const itemOrderNo = useMemo(() => {
    const m = new Map();
    paperItems(paper).forEach((it, i) => {
      const pid = it?.problem_id ?? it?.id;
      if (pid != null && !m.has(String(pid))) m.set(String(pid), i + 1);
    });
    return m;
  }, [paper]);

  // ⛔ 기능 보류(시험 정보) — 추후 기능 오픈 예정
  // 자동 마감이 예약 오픈보다 이르면 열리자마자 닫힌다 — 막지는 않고 결정하는 자리에서 말한다
  // const timeOrderBad = useMemo(() => {
  //   if (!examInfo.openAt || !examInfo.closeAt) return false;
  //   const o = new Date(examInfo.openAt);
  //   const c = new Date(examInfo.closeAt);
  //   return !Number.isNaN(o.getTime()) && !Number.isNaN(c.getTime()) && c <= o;
  // }, [examInfo.openAt, examInfo.closeAt]);

  // const examSummary = useMemo(() => {
  //   const parts = [];
  //   if (examInfo.examType.trim()) parts.push(examInfo.examType.trim());
  //   if (String(examInfo.examRound).trim()) parts.push(`${String(examInfo.examRound).trim()}차`);
  //   if (examInfo.examDate) parts.push(`시험일 ${examInfo.examDate}`);
  //   if (examInfo.openAt) parts.push(`오픈 ${examInfo.openAt.replace('T', ' ')}`);
  //   if (examInfo.closeAt) parts.push(`자동 종료 ${examInfo.closeAt.replace('T', ' ')}`);
  //   return parts.join(' · ');
  // }, [examInfo]);

  const deploy = async () => {
    if (deployingRef.current || picked.length === 0 || paperId == null) return;
    deployingRef.current = true;
    setDeploying(true);
    setToast(null);
    setDeployError(null);
    const sent = picked;
    try {
      const d = await assignPaper(
        paperId,
        // ⛔ 기능 보류(시험 정보) — examInfo 를 싣지 않는다. 되살릴 때 이 인자를 되돌린다:
        //   { studentIds: ..., examInfo }
        { studentIds: sent.map((s) => toIntId(studentId(s))) },
        getAccessToken,
      );
      // 401 이면 쓰기 헬퍼가 /signin 으로 보내며 표식을 돌려준다 — 배포했다고 말하면 거짓말이 된다
      if (d === AUTH_REDIRECT || !aliveRef.current) return;
      const created = (Array.isArray(d?.assigned) ? d.assigned : [])
        .map((a) => a?.assignment_id)
        .filter((id) => id != null);
      setJustCreated(created);
      setPicked([]);
      setBulk(null);
      setChipsExpanded(false);
      setConfirming(false);
      setToast({
        type: 'info',
        text: sent.length === 1
          ? `${studentName(sent[0])}에게 배포했습니다 — 아래 목록에서 채점하러 들어갈 수 있습니다.`
          : `${sent.length}명에게 배포했습니다 — 아래 목록에서 채점하러 들어갈 수 있습니다.`,
      });
      await loadAssignments();
    } catch (e) {
      if (!aliveRef.current) return;
      /**
       * 계약 §1-3 — 배포는 두 갈래로 막힌다.
       *   409 deleted_items       삭제된 문항이 들어 있다 → 그 문항을 빼야 한다
       *   409 missing_difficulty  난이도(분석)가 없는 문항이 있다 → 문제(/problem-hub)에서 분석을 끝내야 한다
       * 둘 다 data.problem_ids 로 **어느 문항인지**를 준다. 문장만 띄우면 사용자가 고칠 방법이
       * 없으므로 번호를 함께 그린다. 404 는 soft 삭제된 학생 id 인데 사유가 안 실려 오므로,
       * 서버 문구를 그대로 흘리고 짚을 곳만 한 줄 덧붙인다.
       */
      const reason = errorReason(e);
      const ids = Array.isArray(e?.data?.problem_ids)
        ? e.data.problem_ids.filter((v) => v != null)
        : [];
      setDeployError({
        text: describeApiError(e, '배포하지 못했습니다 — 다시 시도해 주세요.'),
        hint: (!reason && e?.httpStatus === 404)
          ? '고른 학생 중 삭제된 계정이 섞였을 수 있습니다 — 명단을 비우고 다시 검색해 고르세요.'
          : null,
        items: ids.map((pid) => ({ pid, no: itemOrderNo.get(String(pid)) ?? null })),
      });
    } finally {
      deployingRef.current = false;
      if (aliveRef.current) setDeploying(false);
    }
  };

  const justCreatedKeys = useMemo(() => new Set(justCreated.map((id) => String(id))), [justCreated]);
  // 현재 판과 갈린 배포본 수 — 0 이면 머리줄에 아무 말도 하지 않는다(할 말이 없는 것이 정상이다)
  const staleRevCount = assignments.filter((r) => revDiffers(r)).length;

  return (
    <div className="space-y-3">
      {/* 학생 선택 · 배포 — 페이지의 흰 카드 안이라 여기서 카드를 또 두르지 않는다(흰 위 흰·여백 두 겹) */}
      <div>
        <div className="mb-2 flex items-center gap-1.5">
          <Users aria-hidden="true" className="size-4 text-primary" />
          <h2 className="text-sm font-bold text-gray-800">학생 배포</h2>
        </div>

        {/* 조건 네 칸 — 좁으면 2열 격자, sm 이상은 한 줄. 형제 화면(StudentsHub/StudentTablePanel:417)이
            좁을 때 grid-cols-2 로 접는 그 배치를 그대로 옮겼다. 담당·강의는 v2 학생 API(계약 §7)가
            서버에서 실제로 걸어 주는 필터라, 입력한 대로 결과가 바뀐다. */}
        <div className="flex flex-wrap items-start gap-1.5">
          <div className="grid min-w-40 flex-1 grid-cols-2 gap-1.5 sm:flex sm:flex-wrap">
            <div className="relative w-full sm:min-w-24 sm:flex-1">
              <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-300" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) search(1); }}
                placeholder="학생 이름"
                aria-label="학생 이름"
                className="w-full rounded-lg border py-1.5 pl-8 pr-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <input
              type="text"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) search(1); }}
              placeholder="학교"
              aria-label="학교"
              className="w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none sm:min-w-24 sm:flex-1"
            />
            <input
              type="text"
              value={lecture}
              onChange={(e) => setLecture(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) search(1); }}
              placeholder="강의"
              aria-label="강의"
              className="w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none sm:min-w-24 sm:flex-1"
            />
            {/* 담당 칸 안의 [없음]은 계약의 예약어 in_charge_teacher='none'(§7) 이다 — 「담당 없는
                학생」은 이름으로 적을 수 없는 조건이라 스위치가 따로 있어야 한다. 켜면 입력칸을
                잠근다: 이름과 '없음'을 동시에 보낼 수 없고, 잠그지 않으면 적어 둔 이름이 무시되는
                동안에도 칸에 남아 화면이 거짓말한다. */}
            <div className="relative w-full sm:min-w-24 sm:flex-1">
              <input
                type="text"
                value={noTeacher ? '' : teacher}
                onChange={(e) => setTeacher(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) search(1); }}
                disabled={noTeacher}
                placeholder={noTeacher ? '담당 없는 학생' : '담당 강사'}
                aria-label="담당 강사"
                className="w-full rounded-lg border py-1.5 pl-2 pr-11 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setNoTeacher((v) => !v)}
                aria-pressed={noTeacher}
                title="담당 강사가 없는 학생만 찾습니다"
                className={`absolute right-1 top-1/2 -translate-y-1/2 rounded-full px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                  noTeacher ? 'bg-primary/15 text-primary' : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                없음
              </button>
            </div>
          </div>
          {/* [초기화]는 숨기지 않고 비울 것이 없을 때 비활성으로 둔다 — 나타났다 사라지면
              그때마다 옆의 [검색]이 밀려 누를 자리가 흔들린다. 자리는 늘 같고 상태만 바뀐다. */}
          <div className="flex shrink-0 gap-1.5">
            <Button size="sm" variant="secondary" onClick={resetCond} disabled={loading || !hasCond}>
              초기화
            </Button>
            <Button size="sm" onClick={() => search(1)} disabled={loading}>검색</Button>
          </div>
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <p className="min-w-0 flex-1 text-[10px] text-gray-400">
            조건을 비우고 검색하면 {scopeText} 목록이 나옵니다.
            {scope === 'mine' && ' 이 계정은 담당 학생만 볼 수 있습니다.'}
          </p>
          {/* 총 건수 — 페이지 값이 {page, perPage, totalItems, totalPages} 로 다 온다(§7).
              페이저는 두 쪽부터 뜨는데 "몇 명인가"는 한 쪽일 때가 더 자주 궁금하다. */}
          {searched && !loading && !error && (
            <span className="shrink-0 text-[10px] text-gray-500">총 {rows.totalItems}명</span>
          )}
        </div>

        {/* 검색 결과 */}
        <div className="mt-2">
          {error && (
            <div role="alert" className="space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
              <Button size="xs" variant="secondary" onClick={() => search(rows.page)}>다시 시도</Button>
            </div>
          )}
          {!error && loading && rows.items.length === 0 && (
            <div className="space-y-1.5">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 rounded-3xl bg-gray-100 motion-safe:animate-pulse" />)}
            </div>
          )}
          {!error && !loading && rows.items.length === 0 && (
            <p className="rounded-xl border border-gray-100 p-4 text-center text-sm text-gray-400">
              {searched ? '조건에 맞는 학생이 없습니다 — 조건을 줄여 보세요.' : '학생을 검색해 선택하세요.'}
            </p>
          )}

          {/* 좁은 폭에서는 목록 높이를 묶지 않는다 — 상세 자체가 스크롤러라, 그 안에 또 스크롤러를
              두면 손가락이 어느 쪽을 미는지 알 수 없다 */}
          {/* 결과 전체 담기 — 목록의 **머리줄**이다. 체크 자리를 아래 행과 같은 x 에 세워(둘 다 px-3 +
              size-4) 표 머리처럼 읽히게 했고, 한 단 위라는 것은 회색 바탕·작은 글자로 말한다.
              「지금 쪽 20명」이 아니라 **검색 결과 전체**를 담는다(오너 결정) — 그래서 숫자는 늘
              rows.totalItems 다. 여러 쪽이면 누른 뒤 나머지를 서버에서 더 받아 온다. */}
          {rows.items.length > 0 && (
            <label
              title={bulkOn ? '담은 학생 전체 빼기' : '검색 결과 전체 담기'}
              className="mb-1 flex cursor-pointer items-center gap-2 rounded-3xl bg-gray-50 px-3 py-1.5 transition-colors duration-150 hover:bg-primary/10"
            >
              <input
                type="checkbox"
                ref={bulkCheckRef}
                checked={bulkOn}
                onChange={toggleAllResults}
                disabled={loading || bulkLoading}
                className="size-4 shrink-0 accent-primary"
              />
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-600">
                검색 결과 {rows.totalItems}명 모두 선택
              </span>
              {bulkLoading && (
                <span className="flex shrink-0 items-center gap-1 text-[10px] text-primary">
                  <Loader2 aria-hidden="true" className="size-3 animate-spin" />
                  담는 중
                </span>
              )}
              {/* 여러 쪽일 때만 말한다 — 한 쪽이면 보이는 것이 곧 전부라 설명할 것이 없다 */}
              {!bulkLoading && rows.totalPages > 1 && (
                <span className="shrink-0 text-[10px] text-gray-400">{rows.totalPages}쪽 전부</span>
              )}
            </label>
          )}
          {bulkError && (
            <p role="alert" className="mb-1 px-3 text-[10px] leading-relaxed text-danger">{bulkError}</p>
          )}

          {rows.items.length > 0 && (
            <ul className="space-y-0.5 sm:max-h-64 sm:overflow-y-auto">
              {rows.items.map((s, i) => {
                const sel = isPicked(s);
                const meta = studentMeta(s);
                return (
                  <li key={String(studentId(s) ?? `row-${i}`)}>
                    <label
                      className={`flex cursor-pointer items-center gap-2 rounded-3xl px-3 py-1.5 transition-colors duration-150 ${
                        sel ? 'bg-primary/15' : 'hover:bg-primary/10'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => toggle(s)}
                        className="size-4 shrink-0 accent-primary"
                      />
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-[10px] font-bold leading-none text-white">
                        {studentId(s)}
                      </span>
                      <span className={`min-w-0 flex-1 truncate text-sm ${sel ? 'font-bold text-primary' : 'font-medium text-gray-900'}`}>
                        {studentName(s)}
                      </span>
                      {/* ⛔ 기능 보류(예약생 배지) — 2026-09-05. Q19 회신 뒤 다시 세운다.
                          원래 뜻: 예약 중인 학생(student_status 200) 표시 — 100(일반)에는 아무것도 붙지 않는다.
                          이 목록은 삭제 학생을 서버가 이미 빼고 주므로 퇴원생은 애초에 오지 않는다
                          (BE-20260829 §1 2-1). 글자와 색을 아래 배포 목록의 「예약」(status 50)과
                          갈라 둔 이유는 examPaperApi 의 STUDENT_STATUS 주석에 적어 두었다.

                      {(() => {
                        const sd = studentStatusDisplay(s);
                        if (sd == null) return null;
                        return (
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${sd.cls}`}>{sd.text}</span>
                        );
                      })()}
                      */}
                      {/* 학교·학년 옆에 담당·강의를 잇는다(§7) — 동명이인이 흔해서, 누구를 고르는지
                          가르는 것은 대개 학교가 아니라 담당·강의다. 좁은 폭에서는 이름과 폭을 나눠
                          갖고 잘리므로 title 로 전문을 남긴다. */}
                      {meta && (
                        <span className="min-w-0 max-w-[60%] shrink truncate text-right text-xs text-gray-400" title={meta}>
                          {meta}
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {rows.totalPages > 1 && (
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
              <button
                type="button"
                disabled={loading || rows.page <= 1}
                onClick={() => search(rows.page - 1)}
                aria-label="이전 페이지"
                className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="size-4" />
              </button>
              {rows.page} / {rows.totalPages}
              <button
                type="button"
                disabled={loading || rows.page >= rows.totalPages}
                onClick={() => search(rows.page + 1)}
                aria-label="다음 페이지"
                className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          )}
        </div>

        {/* ⛔ 기능 보류(시험 정보) — 2026-09-05 오너 지시로 잠갔다. 추후 기능 오픈 예정.
            원래 뜻: 시험 정보는 전부 선택값이고 비운 칸은 보내지 않는다(authoring §4).
            아래 원본을 그대로 남긴다 — 되살릴 때 이 주석을 벗기고, 파일 머리 「⛔ 기능 보류」가
            가리키는 나머지 자리(examInfo 상태·setField·timeOrderBad·examSummary·배포 적재·
            CalendarClock import)를 함께 되돌린다. JSX 주석은 겹칠 수 없어 안쪽 설명은 여기로 옮겼다.

        <div className="mt-3 border-t pt-2">
          <div className="mb-1.5 flex items-center gap-1.5">
            <CalendarClock aria-hidden="true" className="size-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-500">시험 정보</span>
            <span className="text-[10px] text-gray-400">전부 선택 — 비운 칸은 보내지 않습니다</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            <label className="text-[10px] text-gray-500">
              시험 구분
              <input
                type="text"
                value={examInfo.examType}
                onChange={(e) => setField('examType', e.target.value)}
                placeholder="중간고사"
                className="mt-0.5 w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
              />
            </label>
            <label className="text-[10px] text-gray-500">
              차수
              <input
                type="number"
                min="1"
                step="1"
                value={examInfo.examRound}
                onChange={(e) => setField('examRound', e.target.value)}
                placeholder="1"
                className="mt-0.5 w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
              />
            </label>
            <label className="text-[10px] text-gray-500">
              시험 일자
              <input
                type="date"
                value={examInfo.examDate}
                onChange={(e) => setField('examDate', e.target.value)}
                className="mt-0.5 w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
              />
            </label>
            <label className="text-[10px] text-gray-500">
              예약 오픈
              <input
                type="datetime-local"
                value={examInfo.openAt}
                onChange={(e) => setField('openAt', e.target.value)}
                className="mt-0.5 w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
              />
            </label>
            <label className="text-[10px] text-gray-500">
              자동 종료
              <input
                type="datetime-local"
                value={examInfo.closeAt}
                onChange={(e) => setField('closeAt', e.target.value)}
                className="mt-0.5 w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
              />
            </label>
          </div>
          <p className="mt-1 text-[10px] leading-relaxed text-gray-400">
            예약 오픈을 <b>미래</b>로 두면 <b>예약</b> 상태로 잡히고 그 시각에 자동으로 열립니다.
            자동 종료 시각이 지나면 배포가 자동으로 종료됩니다 — 확인 주기는 약 60초라 표시가 1분쯤 늦을 수 있습니다.
          </p>
          <p className="mt-0.5 text-[10px] leading-relaxed text-gray-400">
            예약 오픈을 <b>비우면 바로 진행</b>됩니다(수동 오픈은 없습니다). 시각은 이 브라우저의 지역 시간으로 보냅니다.
          </p>
          {timeOrderBad && (
            <p className="mt-1 text-[10px] text-amber-700">
              자동 종료가 예약 오픈보다 빠릅니다 — 열리자마자 종료됩니다.
            </p>
          )}
        </div>
        */}

        {/* 고른 학생 — 페이지를 넘겨도 여기 남는다.
            [출제] **바로 위**에 둔다(오너 지시 2026-08-28) — 누르기 직전에 「누구에게 보내는가」가
            눈에 있어야 한다. 검색칸 아래에 두면 시험 정보 칸에 가려 화면 밖으로 밀린다. */}
        <div className="mt-3 border-t pt-2">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">배포할 학생</span>
            <span className="text-[11px] text-gray-400">{picked.length}명</span>
            {picked.length > 0 && (
              <button
                type="button"
                onClick={() => { setPicked([]); setBulk(null); setChipsExpanded(false); setConfirming(false); }}
                className="ml-auto text-[10px] text-gray-400 underline hover:text-gray-600"
              >
                모두 비우기
              </button>
            )}
          </div>
          {picked.length === 0 ? (
            <p className="text-xs text-gray-400">아직 고른 학생이 없습니다.</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {(chipsExpanded ? picked : picked.slice(0, CHIP_LIMIT)).map((s, i) => (
                <span key={String(studentId(s) ?? `chip-${i}`)} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                  {studentName(s)}
                  {/* ⛔ 기능 보류(예약생 배지) — 2026-09-05. Q19 회신 뒤 다시 세운다.
                      원래 뜻: 고른 뒤에도 신분이 남아야 한다 — 배포를 결정하는 자리는 검색 결과가 아니라
                      이 명단이고, 페이지를 넘기면 위 목록은 사라진다.

                  {(() => {
                    const sd = studentStatusDisplay(s);
                    if (sd == null) return null;
                    return <span className={`rounded-full px-1.5 text-[10px] font-medium ${sd.cls}`}>{sd.text}</span>;
                  })()}
                  */}
                  <button type="button" onClick={() => toggle(s)} aria-label={`${studentName(s)} 제외`} className="hover:text-primary/70">
                    <X className="size-2.5" />
                  </button>
                </span>
              ))}
              {/* 명단이 길면 접는다 — 결과 전체를 담으면 수백 명이 되는데, 칩을 다 그리면 바로 아래
                  [출제]가 화면 밖으로 밀린다. 명단을 [출제] 바로 위에 둔 이유(2026-08-28 오너 지시:
                  누르기 직전에 「누구에게 보내는가」가 눈에 있어야 한다)가 거꾸로 깨진다.
                  숨긴 수는 **적어 둔다** — 접힌 것과 없는 것은 다르다. */}
              {picked.length > CHIP_LIMIT && (
                <button
                  type="button"
                  onClick={() => setChipsExpanded((v) => !v)}
                  className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                >
                  {chipsExpanded ? '접기' : `+${picked.length - CHIP_LIMIT}명 더 보기`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 확인 바 — 스냅샷 고정이 무엇을 뜻하는지 배포 전에 화면에 남긴다 */}
        {confirming && picked.length > 0 && (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="flex items-start gap-1.5 text-xs leading-relaxed text-amber-900">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                <b>{paperTitle || '(제목 미확인)'}</b> 을(를) 학생 <b>{picked.length}명</b>에게 배포합니다.
                {' '}배포 시점의 <b>문항·본문이 스냅샷으로 고정</b>되어, 이후 시험지를 고쳐도 배포본은 지금 이 판을 그대로 기억합니다.
              </span>
            </p>
            {/* ⛔ 기능 보류(시험 정보) — 2026-09-05. 추후 기능 오픈 예정.
                보낼 시험 정보가 없으니 늘 「없음」만 뜬다 — 확인 바에서 통째로 뺀다.

            <p className="mt-1.5 text-xs leading-relaxed text-amber-900">
              시험 정보: {examSummary || '없음(비운 칸은 보내지 않습니다)'}
              {examInfo.openAt ? ' — 예약 상태로 잡히고 그 시각에 자동으로 열립니다.' : ' — 예약 오픈이 없어 바로 진행됩니다.'}
            </p>
            */}
            {/* 스냅샷은 서버에 저장된 판을 굳힌다 — 결정을 내리는 자리에서 말해야 뜻이 있다.
                막지는 않는다: 저장 없이 지금 판을 그대로 배포하는 것도 정당한 선택이다. */}
            {paperDirty && (
              <p className="mt-1.5 rounded-lg bg-amber-100/70 px-2 py-1.5 text-xs leading-relaxed text-amber-900">
                <b>저장하지 않은 문항 변경은 이 배포본에 담기지 않습니다</b> — 배포본은 서버에 저장된 판을 굳힙니다.
                {' '}지금 고친 내용까지 보내려면 [문항] 탭에서 먼저 저장한 뒤 배포하세요.
              </p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" onClick={deploy} loading={deploying} disabled={deploying} icon={<Send className="size-3.5" />}>
                출제 확정
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirming(false)} disabled={deploying}>취소</Button>
            </div>
          </div>
        )}

        {!confirming && (
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setConfirming(true)}
              disabled={picked.length === 0 || deploying || paperId == null}
              icon={<Send className="size-3.5" />}
            >
              출제
            </Button>
            <span className="text-[11px] text-gray-400">한 번 더 확인한 뒤 보냅니다.</span>
          </div>
        )}

        {/* 배포가 막힌 이유 — 문장 한 줄로 흘리지 않는다. 409 는 걸린 문항까지 알려 주므로(§1-3)
            그 번호를 그려야 사용자가 [문항] 탭에서 빼거나 분석을 돌릴 수 있다. */}
        {deployError && (
          <div role="alert" className="mt-2 rounded-xl border border-red-100 bg-red-50 p-3">
            <p className="flex items-start gap-1.5 text-xs leading-relaxed text-red-700">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>{deployError.text}</span>
            </p>
            {deployError.hint && (
              <p className="mt-1.5 text-xs leading-relaxed text-red-700">{deployError.hint}</p>
            )}
            {deployError.items.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {deployError.items.map(({ pid, no }) => (
                  <span
                    key={String(pid)}
                    title={no != null ? `${no}번 문항 (문제 #${pid})` : `문제 #${pid} — 이 시험지의 현재 문항 목록에 없습니다`}
                    className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700"
                  >
                    {/* 번호를 못 찾는 경우 — 다른 곳에서 문항이 바뀌었거나 상세를 아직 못 읽은 때다.
                        그때도 문제 번호는 보여 준다(없는 것보다 낫다). */}
                    {no != null ? `${no}번` : '문항'}
                    {/* 색은 칩이 갖는다(빨강 = 배포를 막은 것) — 번호는 고정폭·굵기만 가져오고
                        누르면 그 문제로 간다. 여기서 할 일이 「그 문제를 고치거나 빼기」라서다. */}
                    <ProblemIdLink id={pid} chip />
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2">
              <Button size="xs" variant="secondary" onClick={() => setDeployError(null)}>닫기</Button>
            </div>
          </div>
        )}

        <p
          role={toast?.type === 'error' ? 'alert' : 'status'}
          aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
          className={`mt-1.5 text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
        >
          {toast?.text || ''}
        </p>
      </div>

      {/* 배포 목록 — 카드 대신 선 하나로 가른다 */}
      <div className="border-t pt-3">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-bold text-gray-800">출제 이력</h3>
          <span className="text-[11px] text-gray-400">{assignments.length > 0 ? `${assignments.length}건` : ''}</span>
          <button
            type="button"
            onClick={loadAssignments}
            disabled={listLoading || paperId == null}
            aria-label="배포 목록 새로고침"
            title="새로고침"
            className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <RefreshCw className={`size-3.5 ${listLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* 판이 갈린 배포본이 있다는 **사실**을 말하는 자리. 경고가 아니다 — 배포본이 배포 시점 판을
            그대로 붙들고 있는 것은 사고가 아니라 계약이 확인해 준 정상 동작이다(회신 §1-1).
            그래서 amber 띠를 두르지 않고 머리줄의 회색 보조 글자로만 둔다. 배포 **전**에는 같은 사실을
            확인 바가 말하는데(위 「스냅샷으로 고정」), 배포 뒤에 그 말을 하는 자리가 여기까지 없었다.

            두 문장으로 가른 이유: 「굳는다」는 **모든 배정본**의 성질이고(회신 §1-1 — 시험지 revision 이
            올라가도 기존 배정의 paper_revision·problems_info 는 그대로), N 은 **지금 몇 건이 현재 판과
            다른가**라는 별개의 사실이다. 한 문장에 섞어 「아래 N건은 바뀌지 않습니다」라고 쓰면 나머지
            배포본은 시험지를 고치면 따라 바뀐다는 뜻이 된다 — 계약에 없는 말이다.
            N=0 일 때 통째로 숨기는 것은 그대로 둔다. 굳는다는 성질은 배포 **전** 확인 바가 이미 말하므로,
            여기서 말할 값이 있을 때만(갈린 것이 있을 때만) 입을 연다. */}
        {staleRevCount > 0 && (
          <p className="mb-2 text-[11px] leading-relaxed text-gray-400">
            배포본은 배포 시점 판으로 굳습니다 — 시험지를 고쳐도 이미 나간 배포본은 바뀌지 않습니다.
            이 시험지는 지금 rev {currentRevision} 이고, 그와 다른 판으로 나간 배포본이 {staleRevCount}건 있습니다.
          </p>
        )}

        {listError && (
          <div role="alert" className="space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
            <p className="text-sm text-red-700">{listError}</p>
            <Button size="xs" variant="secondary" onClick={loadAssignments}>다시 시도</Button>
          </div>
        )}

        {!listError && listLoading && assignments.length === 0 && (
          <div className="space-y-1.5">
            {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
          </div>
        )}

        {/* 시험 전체 결과 — **마감된 배포본만** 센다(레거시 ExamsByPresetModal:83 과 같은 규칙).
            채점 중인 배정을 섞으면 아직 안 매긴 문항이 오답처럼 깔려 정답률이 실제보다 낮게 나온다.
            마감분이 0 건이면 숫자 대신 그 사실을 적는다 — 0% 라고 쓰면 「전부 틀렸다」로 읽힌다. */}
        {!listError && assignments.length > 0 && (() => {
          const sum = assignmentsSummary(assignments);
          return (
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 whitespace-nowrap rounded-xl bg-gray-50 px-4 py-3 text-xs">
              <span className="shrink-0 font-semibold text-gray-800">시험 전체 결과</span>
              {sum.closedCount === 0 ? (
                <span className="text-gray-500">아직 채점을 완료한 배포본이 없습니다 — 완료하면 여기에 합계가 섭니다.</span>
              ) : (
                <>
                  <span className="shrink-0 text-gray-400">채점 완료 {sum.closedCount}명 기준</span>
                  <div className="ml-auto flex shrink-0 flex-wrap items-center gap-4">
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">정답</span>
                      <span className="font-semibold text-green-600">{sum.correct}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">오답</span>
                      <span className="font-semibold text-red-600">{sum.wrong}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">총문제</span>
                      <span className="font-semibold text-gray-900">{sum.total}</span>
                    </span>
                    {/* 분모는 「총문제」가 아니라 채점한 문항(정답+오답)이다 — 위 칸들과 셈이 안 맞아
                        보이므로 무엇을 나눈 값인지 title 로 말한다(examPaperApi:accuracyBasisText) */}
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">전체 정답률</span>
                      <span
                        className={`text-sm font-bold ${accuracyTone(sum.rate)}`}
                        title={accuracyBasisText(sum.correct, sum.wrong, sum.total)}
                      >
                        {sum.rate != null ? `${sum.rate}%` : '—'}
                      </span>
                    </span>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {!listError && !listLoading && assignments.length === 0 && (
          <p className="rounded-xl border border-gray-100 p-4 text-center text-xs text-gray-500">
            아직 이 시험지를 받은 학생이 없습니다 — 위에서 학생을 골라 배포하세요.
          </p>
        )}

        {assignments.length > 0 && (
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[880px] text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {HISTORY_COLUMNS.map((c) => {
                    const thClass = `whitespace-nowrap px-3 py-2 ${c.align} text-xs font-medium tracking-wider text-gray-500`;
                    if (c.sortable === false) {
                      return <th key={c.key} scope="col" className={thClass}>{c.label}</th>;
                    }
                    return (
                      <SortableTh key={c.key} sortKey={c.key} sort={historySort} onSort={onHistorySort} className={thClass}>
                        {c.label}
                      </SortableTh>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white">
                {sortedAssignments.map((r, i) => {
                  const aid = r?.assignment_id ?? null;
                  const st = assignStatusDisplay(r);
                  const isNew = aid != null && justCreatedKeys.has(String(aid));
                  // 삭제된 학생이면 신상이 통째로 null 이다(회신 §1) — 배지는 '-', 이름은 링크가 아니다
                  const sid = r?.student_id ?? null;
                  const cnt = assignmentCounts(r);
                  const rate = accuracyOf(r);
                  const school = studentSchoolText(r);
                  const klass = studentClassText(r);
                  const open = aid != null ? () => onOpenAssignment?.(aid) : undefined;
                  return (
                    <tr
                      key={String(aid ?? `row-${i}`)}
                      onClick={open}
                      className={`border-b border-gray-100 transition-colors duration-150 last:border-b-0 ${
                        aid == null ? 'opacity-50' : 'cursor-pointer hover:bg-primary/10'
                      }`}
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-center font-mono text-xs text-gray-400">
                        {aid != null ? `#${aid}` : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        <StudentIdBadge id={sid} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <StudentNameLink id={sid} name={r?.student_name} />
                        {isNew && (
                          <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">방금 배포</span>
                        )}
                      </td>
                      {/* 학생 신상은 삭제된 학생이면 통째로 null 로 온다(회신 §1) — 빈 칸 대신 '—' 를 적어
                          「값이 없다」와 「안 그려졌다」를 가른다 */}
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">{school || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">{klass || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>{st.text}</span>
                        {/* 예약은 「언제 열리는가」가 곧 상태의 내용이다 — 열을 늘리지 않고 배지 아래 붙인다 */}
                        {r?.status === 50 && r?.open_at && (
                          <span className="mt-0.5 block text-[10px] text-gray-400">{fmtServerDateTime(r.open_at)} 오픈</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        {/* 판 꼬리표 — 얼굴은 시험지 표(PaperTable)의 rev 알약을 글자까지 그대로 가져왔다.
                            현재 판과 갈렸을 때는 칩을 둘로 늘리지 않고 title 로 말한다. */}
                        {r?.paper_revision != null ? (
                          <span
                            className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600"
                            title={revDiffers(r)
                              ? `배포 시점 판 — 지금 시험지는 rev ${currentRevision} 입니다`
                              : '배포 시점 판'}
                          >
                            rev {r.paper_revision}
                          </span>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                      {/* 정답·오답·총문제. graded 를 쓰지 않는다 — 그 값은 오답과 미채점까지 세는
                          「채점 기록이 있는 문항 수」다(계약 §2-9). 셋을 못 받았으면 진행률만 적는다. */}
                      <td className="whitespace-nowrap px-3 py-2 text-center text-gray-900">
                        {cnt.correct != null && cnt.wrong != null ? (
                          <>
                            <span className="text-green-600">{cnt.correct}</span>
                            {' / '}
                            <span className="text-red-600">{cnt.wrong}</span>
                            {' / '}
                            <span>{cnt.total ?? 0}</span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">채점 {cnt.graded ?? 0}/{cnt.total ?? 0}</span>
                        )}
                      </td>
                      {/* 정답률은 마감분에만 그린다 — 진행 중에는 채점이 늘 때마다 출렁이는 값이라
                          보는 사람이 그것을 실력으로 읽는다(레거시와 같은 규칙).
                          분모는 왼쪽 칸의 「총」이 아니라 채점한 문항(정답+오답)이라, 셈의 근거와
                          「—」의 이유를 각각 title 로 말한다(examPaperApi 의 두 문장을 그대로 쓴다) */}
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        {rate != null
                          ? (
                            <span
                              className={`font-medium ${accuracyTone(rate)}`}
                              title={accuracyBasisText(cnt.correct, cnt.wrong, cnt.total)}
                            >
                              {rate}%
                            </span>
                          )
                          : <span className="text-xs text-gray-400" title={accuracyEmptyText(r)}>—</span>}
                      </td>
                      {/* 마감일 — 마감됐으면 실제 마감 시각, 아직이면 걸려 있는 자동 종료 예정을 말한다.
                          둘은 다른 값이다(closed_at 은 지나간 일, close_at 은 앞으로 올 일). */}
                      <td className="whitespace-nowrap px-3 py-2 text-center text-gray-600">
                        {r?.closed_at
                          ? fmtServerDateShort(r.closed_at)
                          : (r?.close_at
                            ? <span className="text-[10px] text-gray-400" title={`자동 종료 예정 ${fmtServerDateTime(r.close_at)}`}>~{fmtServerDateShort(r.close_at)}</span>
                            : <span className="text-xs text-gray-400">—</span>)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); open?.(); }}
                          disabled={aid == null}
                          title="채점 창 열기"
                          className="inline-flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ExternalLink aria-hidden="true" className="size-3" />
                          결과
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
